import { ApiError } from '#core/composables/useApiFetch'
import { FORGD_SYSTEM_PROJECT_ID } from '@forgd/shared/constants'
import { useQuery } from '@pinia/colada'

/**
 * Whether the MMM dashboard should present demo (system project) data.
 * `undefined` = not resolved yet; consumers should keep their queries disabled.
 */
export function useMmmIsDemoProject() {
  const route = useRoute()
  const auth = useAuth()

  const urlProjectId = computed(() => {
    return Array.isArray(route.params.projectId)
      ? route.params.projectId[0]
      : route.params.projectId
  })

  const isOwnProject = computed(() => {
    return auth.isLoggedIn
      && Boolean(urlProjectId.value)
      && urlProjectId.value === auth.project?.id
  })

  const { data: mmProject, error } = useQuery({
    ...buildApiQuery('/liquidity-monitoring/for-project/mm-project/{projectId}', {
      params: () => ({
        path: {
          projectId: String(urlProjectId.value),
        },
      }),
    }),
    enabled: () => isOwnProject.value && !auth.project?.isResearch,
    refetchOnWindowFocus: false,
  })

  return computed<boolean | undefined>(() => {
    if (!urlProjectId.value || urlProjectId.value === FORGD_SYSTEM_PROJECT_ID || !auth.isLoggedIn) {
      return true
    }

    if (!isOwnProject.value) {
      return false
    }

    // Research projects can't have engagements — always demo.
    if (auth.project?.isResearch) {
      return true
    }

    if (error.value) {
      // 404 = the project never entered the MM flow - demo project
      return error.value instanceof ApiError && error.value.status === 404 ? true : undefined
    }

    if (!mmProject.value) {
      return undefined
    }

    return !mmProject.value.isMarketMakerMonitoringEnabled
  })
}
